import { useRef, useCallback } from 'react';
import { useFileDrop } from '@/hooks/useFileDrop';

/**
 * Generic drag-and-drop + click-to-browse file picker.
 *
 * Owns only the *mechanics*: drag-over state, click-to-open, the hidden
 * <input>, and the busy/disabled state. It is deliberately content-agnostic —
 * pass the idle UI as `children` and the busy UI as `busyContent`.
 *
 * Validation, upload, toasts, and feature-specific copy belong in the parent:
 * `onFiles` receives the RAW files chosen (drop or picker); validate there.
 *
 * Reuse this for ANY file-upload UI (receipts, job photos, documents, …)
 * instead of re-implementing drag/drop handling. See ReceiptDropZone for an
 * example wrapper.
 */
export interface FileDropZoneProps {
  /** Called with the raw files chosen via drop or the file picker. Validate in the parent. */
  onFiles: (files: File[]) => void;
  /** `accept` attribute for the hidden file input (e.g. "image/*,application/pdf"). */
  accept?: string;
  /** Allow selecting more than one file. Default true. */
  multiple?: boolean;
  /** When true, drag/click are disabled and `busyContent` is shown in place of children. */
  busy?: boolean;
  /** Idle-state content (icon + copy). */
  children: React.ReactNode;
  /** Content shown while `busy` (e.g. spinner + progress). Falls back to `children`. */
  busyContent?: React.ReactNode;
  /** Extra classes merged onto the dashed drop target. */
  className?: string;
}

export function FileDropZone({
  onFiles,
  accept,
  multiple = true,
  busy = false,
  children,
  busyContent,
  className = '',
}: FileDropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { isDragOver, dropHandlers } = useFileDrop({ onFiles, disabled: busy });

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onFiles(Array.from(e.target.files ?? []));
    e.target.value = '';
  }, [onFiles]);

  return (
    <div
      {...(busy ? {} : dropHandlers)}
      onClick={busy ? undefined : () => inputRef.current?.click()}
      className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors ${
        busy
          ? 'border-indigo-300 bg-indigo-50/50 cursor-default'
          : isDragOver
            ? 'border-indigo-400 bg-indigo-50 cursor-pointer'
            : 'border-gray-300 hover:border-gray-400 cursor-pointer'
      } ${className}`}
    >
      {busy ? (busyContent ?? children) : children}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleInputChange}
        className="hidden"
      />
    </div>
  );
}
